import React, { useEffect, useState } from 'react';
import { Users, CheckCircle, Clock, List, ClipboardList, PiggyBank } from 'lucide-react';
import { apiClient } from '@/api/client';
import { callCenterApi } from '@/api/callcenter';
import { useAuthStore } from '@/store/auth';

const CallCenterDashboard = () => {
  const { user } = useAuthStore();
  const role = user?.role;
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [invLoading, setInvLoading] = useState(false);
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(10);
  const [invTotal, setInvTotal] = useState(0);
  const [invItems, setInvItems] = useState<any[]>([]);
  const [filters, setFilters] = useState<{ search?: string; status?: string; bank_id?: number }>(()=>({}));

  const fetchSummary = async () => {
    setLoading(true);
    try {
      if (role === 'staff') {
        const summary = await callCenterApi.summary.me();
        setData(summary);
      } else {
        const summary = await callCenterApi.summary.team();
        setData(summary);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, [role]);

  const fmt = (n: number) => (Number(n || 0).toLocaleString('tr-TR'));
  const fmtMoney = (n: number) => `€ ${fmt(n)}`;

  const loadInvestments = async () => {
    setInvLoading(true);
    try {
      const res = role === 'staff'
        ? await callCenterApi.investments.my({ page: invPage, page_size: invPageSize, status: filters.status, bank_id: filters.bank_id, search: filters.search })
        : await callCenterApi.investments.list({ page: invPage, page_size: invPageSize, status: filters.status, bank_id: filters.bank_id, search: filters.search });
      setInvItems(res.items || []);
      setInvTotal(res.total || 0);
    } catch { /* ignore */ } finally { setInvLoading(false); }
  };

  useEffect(() => { loadInvestments(); }, [invPage, invPageSize, filters.status, filters.bank_id, filters.search, role]);

  const t = (tr: string, de: string) => (role === 'staff' ? de : tr);

  return (
    <div className="p-6 space-y-6">
      <div className="bg-primary-950 border-l-4 border-gold-500 p-6 text-white">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-400 mb-1.5 block">Sky View GmbH</span>
        <h1 className="text-2xl font-serif font-medium mb-1.5">{t('Call Center Dashboard', 'Callcenter-Dashboard')}</h1>
        <p className="text-primary-200 text-sm">{t('Takım özeti', 'Teamübersicht')}{role === 'staff' ? ' – Persönliche Übersicht' : ''}</p>
      </div>

      {/* Investments highlight */}
      <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Toplam Yatırım Tutarı', 'Gesamte Anlagesumme')}</p>
            <p className="text-3xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">€ {fmt(data?.investments_overview?.total_invested_amount || 0)}</p>
            <p className="mt-1.5 text-sm text-slate-500">{t('Toplam', 'Gesamt')}: {fmt(data?.investments_overview?.total_investments || 0)} • {t('Aktif', 'Aktiv')}: {fmt(data?.investments_overview?.active_investments || 0)} • {t('Tamamlanan', 'Abgeschlossen')}: {fmt(data?.investments_overview?.matured_investments || 0)}</p>
          </div>
          <div className="w-12 h-12 bg-gold-50 flex items-center justify-center shrink-0">
            <PiggyBank className="w-6 h-6 text-gold-700"/>
          </div>
        </div>
      </div>

      {/* Investments list with filters */}
      <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-lg font-serif font-medium text-slate-900">{t('Takım Yatırımları', 'Team-Anlagen')}{role === 'staff' ? ' (Meine Kunden)' : ''}</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder={t('Ara (müşteri/banka)', 'Suchen (Kunde/Bank)')}
              className="px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              onChange={(e)=> setFilters(f=>({ ...f, search: e.target.value }))}
            />
            <select className="px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" value={filters.status || ''} onChange={(e)=> setFilters(f=>({ ...f, status: e.target.value || undefined }))}>
              <option value="">{t('Durum (hepsi)', 'Status (alle)')}</option>
              <option value="active">{t('Aktif', 'Aktiv')}</option>
              <option value="matured">{t('Tamamlanan', 'Abgeschlossen')}</option>
              <option value="cancelled">{t('İptal', 'Storniert')}</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">{t('Tarih', 'Datum')}</th>
                <th className="py-2 pr-4">{t('Müşteri', 'Kunde')}</th>
                <th className="py-2 pr-4">{t('Banka', 'Bank')}</th>
                <th className="py-2 pr-4">{t('Tutar', 'Betrag')}</th>
                <th className="py-2 pr-4">{t('Vade (Ay)', 'Laufzeit (Monat)')}</th>
                <th className="py-2 pr-4">{t('Faiz', 'Zins')}</th>
                <th className="py-2 pr-4">{t('Durum', 'Status')}</th>
              </tr>
            </thead>
            <tbody>
              {invLoading ? (
                <tr><td colSpan={7} className="py-4 text-center text-slate-500">{t('Yükleniyor...', 'Lädt...')}</td></tr>
              ) : invItems.length === 0 ? (
                <tr><td colSpan={7} className="py-4 text-center text-slate-500">{t('Kayıt yok', 'Keine Daten')}</td></tr>
              ) : (
                invItems.map((it:any)=> (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-4 text-slate-700">{it.start_date?.slice(0,10) || '-'}</td>
                    <td className="py-2 pr-4 text-slate-900">{it.customer_name || `#${it.customer_id}`}</td>
                    <td className="py-2 pr-4 text-slate-700">{it.bank_name || `#${it.bank_id}`}</td>
                    <td className="py-2 pr-4 text-slate-900 tabular-nums">{fmtMoney(it.amount || 0)}</td>
                    <td className="py-2 pr-4 text-slate-700">{it.duration_months ?? '-'}</td>
                    <td className="py-2 pr-4 text-slate-700">%{it.interest_rate}</td>
                    <td className="py-2 pr-4 text-slate-700">{it.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-sm text-slate-500">{t('Toplam', 'Gesamt')} {invTotal.toLocaleString('tr-TR')}</div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent" disabled={invPage<=1} onClick={()=> setInvPage(p=>p-1)}>{t('Önceki', 'Zurück')}</button>
            <span className="text-sm text-slate-600">{t('Sayfa', 'Seite')} {invPage}</span>
            <button className="px-3 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent" disabled={(invPage*invPageSize)>=invTotal} onClick={()=> setInvPage(p=>p+1)}>{t('Sonraki', 'Weiter')}</button>
          </div>
        </div>
      </div>

      {role !== 'staff' ? (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Atanmış Müşteriler', 'Zugewiesene Kunden')}</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{data?.assigned_customers ?? 0}</p>
            </div>
            <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-success-600"/>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Atanmamış Müşteriler', 'Nicht zugewiesene Kunden')}</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{data?.unassigned_customers ?? 0}</p>
            </div>
            <div className="w-11 h-11 bg-warning-50 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-warning-700"/>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Toplam Müşteri', 'Gesamt Kunden')}</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{data?.total_customers ?? 0}</p>
            </div>
            <div className="w-11 h-11 bg-primary-50 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-primary-700"/>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Personel', 'Mitarbeiter')}</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{data?.total_agents ?? 0}</p>
            </div>
            <div className="w-11 h-11 bg-gold-50 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-gold-700"/>
            </div>
          </div>
        </div>
      </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Atanmış Müşterilerim', 'Meine zugewiesenen Kunden')}</p>
              <p className="text-2xl font-serif font-medium text-slate-900 mt-1.5 tabular-nums">{data?.assigned_customers ?? 0}</p>
            </div>
            <div className="w-11 h-11 bg-success-50 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-success-600"/>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-primary-600">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-serif font-medium text-slate-900">{t('Son Atamalar', 'Letzte Zuweisungen')}{role === 'staff' ? '' : ''}</h2>
            <List className="w-5 h-5 text-slate-400"/>
          </div>
          {!data?.recent_assignments || data.recent_assignments.length === 0 ? (
            <p className="text-slate-500 text-sm">{t('Kayıt yok', 'Keine Daten')}</p>
          ) : (
            <div className="space-y-2">
              {data.recent_assignments.map((a:any)=> (
                <div key={a.id} className="p-3 border border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{t('Müşteri:', 'Kunde:')} {a.customer_full_name || `#${a.customer_id}`} (ID: {a.customer_id}){role !== 'staff' ? ` → ${t('Personel:', 'Personal:')} ${a.agent_full_name || `#${a.agent_id}`} (ID: ${a.agent_id})` : ''}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{a.assigned_at}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white p-6 border border-slate-200 border-t-2 border-t-gold-500">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-serif font-medium text-slate-900">{t('Son Notlar', 'Letzte Notizen')}{role === 'staff' ? t('Benim Son Notlarım', 'Meine letzten Notizen') : ''}</h2>
            <ClipboardList className="w-5 h-5 text-slate-400"/>
          </div>
          {!data?.recent_notes || data.recent_notes.length === 0 ? (
            <p className="text-slate-500 text-sm">{t('Kayıt yok', 'Keine Daten')}</p>
          ) : (
            <div className="space-y-2">
              {data.recent_notes.map((n:any)=> (
                <div key={n.id} className="p-3 border border-slate-200">
                  <p className="text-sm font-medium text-slate-900">{n.note_text}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t('Müşteri:', 'Kunde:')} {n.customer_full_name || `#${n.customer_id}`} (ID: {n.customer_id}){role !== 'staff' ? ` • ${t('Personel:', 'Personal:')} ${n.agent_full_name || `#${n.agent_id}`} (ID: ${n.agent_id})` : ''} • {n.created_at}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallCenterDashboard;
